import { RFP_FORM } from "@platform/rfp-core";

const {
  EFFECTIVE_DATE,
  DUE_DATE,
  CLIENT_TYPE,
  HAS_BEN_ADMIN_SYSTEM_STATUS,
  REQUESTED_SYSTEM,
  REQUESTED_SYSTEM_OTHER,
  TECH_IMPLEMENTATION,
  TECH_CREDIT_TYPE,
  TECH_CREDIT_AMOUNT,
  EN_CENSUS_AVAILABLE,
  EN_CENSUS_FEATURE_ENABLED,
  EN_CENSUS_FOR_PROSPECTS,
  EN_PUSH_SOLD_PLAN_DATA,
  EN_PUSH_SOLD_PLAN_DATA_FEATURE_ENABLED,
} = RFP_FORM;

interface Coverage {
  name: string;
  [key: string]: unknown;
}

type RfpState = Record<string, unknown>;

type ValidationFunction = (
  value: unknown,
  state: RfpState,
) => string | null | false;

type ValidityFunction = (value: unknown, state: RfpState) => boolean;

const isBenAdminInPlace = (value: unknown): boolean => value === "1";
const isEmployeeNavigatorSelected = (state: RfpState): boolean =>
  state[REQUESTED_SYSTEM] === "employeeNavigator";
const isClientEligibleForEnCensus = (state: RfpState): boolean =>
  state[CLIENT_TYPE] === "current_client" ||
  state[EN_CENSUS_FOR_PROSPECTS] === true;

const isEmployeeNavigatorCensusRequired = (state: RfpState): boolean =>
  isBenAdminInPlace(state[HAS_BEN_ADMIN_SYSTEM_STATUS]) &&
  state[EN_CENSUS_FEATURE_ENABLED] === true &&
  isClientEligibleForEnCensus(state) &&
  isEmployeeNavigatorSelected(state);

const isEmployeeNavigatorPushSoldPlanDataRequired = (
  state: RfpState,
): boolean =>
  isBenAdminInPlace(state[HAS_BEN_ADMIN_SYSTEM_STATUS]) &&
  state[EN_PUSH_SOLD_PLAN_DATA_FEATURE_ENABLED] === true &&
  isClientEligibleForEnCensus(state) &&
  isEmployeeNavigatorSelected(state);

export const validationByStep = (
  selectedCoverages: Coverage[] | undefined,
  selectedCarriers: string[] | undefined,
  isDocumentsValid: boolean,
  centroCarrierSelected = false,
): Record<number, boolean> => {
  let disableCarrierStep = !selectedCarriers?.length;
  if (centroCarrierSelected) {
    disableCarrierStep = false;
  }
  return {
    1: !selectedCoverages?.length,
    3: disableCarrierStep,
    4: !isDocumentsValid,
  };
};

export const rfpInfoValidations: Record<string, ValidationFunction> = {
  [EFFECTIVE_DATE]: (value, state) =>
    value <= state[DUE_DATE] && "Please enter a date past the due date.",
  [DUE_DATE]: (value, state) => {
    const effectiveDate = new Date(state[EFFECTIVE_DATE] as string);
    const defaultDate = new Date(0);
    return (
      !isNaN(effectiveDate.getTime()) &&
      effectiveDate <= value &&
      effectiveDate.getTime() !== defaultDate.getTime() &&
      "Please enter a date prior to the effective date."
    );
  },
  [REQUESTED_SYSTEM]: (value, state) =>
    isBenAdminInPlace(state[HAS_BEN_ADMIN_SYSTEM_STATUS]) &&
    !value &&
    "Ben Admin System is required.",
  [REQUESTED_SYSTEM_OTHER]: (value, state) =>
    state[REQUESTED_SYSTEM] === "other" &&
    !(value as string)?.trim() &&
    "Please specify the Ben Admin System.",
  [TECH_CREDIT_TYPE]: (value, state) =>
    state[TECH_IMPLEMENTATION] === "1" && !value && "Credit Type is required.",
  [TECH_CREDIT_AMOUNT]: (value, state) => {
    const creditType = state[TECH_CREDIT_TYPE] as string;
    if (
      state[TECH_IMPLEMENTATION] !== "1" ||
      creditType === "carrierStandard" ||
      !creditType
    ) {
      return null;
    }
    if (!value || (value as number) <= 0) {
      return "Amount must be greater than 0.";
    }
    if (
      (creditType === "percentage" || creditType === "pepm") &&
      (value as number) > 100
    ) {
      return "Amount cannot exceed 100.";
    }
    return null;
  },
  [EN_CENSUS_AVAILABLE]: (value, state) =>
    isEmployeeNavigatorCensusRequired(state) &&
    !value &&
    "Census availability is required.",
  [EN_PUSH_SOLD_PLAN_DATA]: (value, state) =>
    isEmployeeNavigatorPushSoldPlanDataRequired(state) &&
    !value &&
    "Push sold plan data selection is required.",
};

export const isRfpFormValid: Record<string, ValidityFunction> = {
  [DUE_DATE]: (value, state) => {
    if (!value) return false;
    const today = new Date();
    const DueDate = new Date(value as string);
    const effectiveDateObj = new Date(state[EFFECTIVE_DATE] as string);
    today.setHours(0, 0, 0, 0);
    return DueDate >= today || DueDate > effectiveDateObj;
  },
  [EFFECTIVE_DATE]: (value, state) => {
    const effectiveDateObj = new Date(value as string);
    const dueDateObj = new Date(state[DUE_DATE] as string);
    return effectiveDateObj > dueDateObj;
  },
  [HAS_BEN_ADMIN_SYSTEM_STATUS]: (value, state) => {
    // If Yes, must have system selected
    if (isBenAdminInPlace(value)) {
      if (!state[REQUESTED_SYSTEM]) return false;
      // If "other", must have custom text
      if (
        state[REQUESTED_SYSTEM] === "other" &&
        !(state[REQUESTED_SYSTEM_OTHER] as string)?.trim()
      ) {
        return false;
      }
    }
    return true;
  },
  [REQUESTED_SYSTEM]: (value, state) => {
    // Required if Ben Admin is in place
    if (isBenAdminInPlace(state[HAS_BEN_ADMIN_SYSTEM_STATUS])) {
      return !!value;
    }
    return true;
  },
  [REQUESTED_SYSTEM_OTHER]: (value, state) => {
    // Required if "other" is selected
    if (state[REQUESTED_SYSTEM] === "other") {
      return !!(value as string)?.trim();
    }
    return true;
  },
  [TECH_IMPLEMENTATION]: (value, state) => {
    // If Yes (value === "1"), must have credit type
    if (value === "1") {
      if (!state[TECH_CREDIT_TYPE]) return false;
      // If not carrier standard, must have amount
      if (
        state[TECH_CREDIT_TYPE] !== "carrierStandard" &&
        !state[TECH_CREDIT_AMOUNT]
      ) {
        return false;
      }
      // Validate amount range
      const amount = parseFloat(state[TECH_CREDIT_AMOUNT] as string);
      if (
        state[TECH_CREDIT_TYPE] !== "carrierStandard" &&
        (isNaN(amount) || amount <= 0)
      ) {
        return false;
      }
      if (
        (state[TECH_CREDIT_TYPE] === "percentage" ||
          state[TECH_CREDIT_TYPE] === "pepm") &&
        amount > 100
      ) {
        return false;
      }
    }
    return true;
  },
  [TECH_CREDIT_TYPE]: (value, state) => {
    // Required if tech credits needed
    if (state[TECH_IMPLEMENTATION] === "1") {
      return !!value;
    }
    return true;
  },
  [TECH_CREDIT_AMOUNT]: (value, state) => {
    // Required if tech credit type requires amount
    const creditType = state[TECH_CREDIT_TYPE] as string;
    if (
      state[TECH_IMPLEMENTATION] === "1" &&
      creditType &&
      creditType !== "carrierStandard"
    ) {
      const amount = parseFloat(value as string);
      if (!value || isNaN(amount) || amount <= 0) return false;
      if (
        (creditType === "percentage" || creditType === "pepm") &&
        amount > 100
      ) {
        return false;
      }
      return true;
    }
    return true;
  },
  [EN_CENSUS_AVAILABLE]: (value, state) => {
    if (!isEmployeeNavigatorCensusRequired(state)) {
      return true;
    }

    return !!value;
  },
  [EN_PUSH_SOLD_PLAN_DATA]: (value, state) => {
    if (!isEmployeeNavigatorPushSoldPlanDataRequired(state)) {
      return true;
    }

    return !!value;
  },
};
